import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Pagination,
  Divider,
  ListItemAvatar,
  Avatar,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  AccountCircle as AccountCircleIcon,
  RateReview as RateReviewIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import api from "../../api";

const FeedbackViewer = ({ upload, onBack }) => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [showOnlyNotApproved, setShowOnlyNotApproved] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [availableCategories, setAvailableCategories] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        let url = `/feedback/${upload.id}?page=${page}`;
        if (showOnlyNotApproved) {
          url += "&status=not_approved";
        }
        if (selectedCategory && selectedCategory !== "all") {
          url += `&category=${encodeURIComponent(selectedCategory)}`;
        }
        const res = await api.get(url);
        setFeedback(res.data.items || []);
        setTotalPages(res.data.totalPages || 0);
        if (Array.isArray(res.data.categories)) {
          setAvailableCategories(res.data.categories);
        }
        setError("");
      } catch (err) {
        console.error("Fetch feedback error:", err);
        setError("Failed to fetch feedback.");
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [upload.id, page, showOnlyNotApproved, selectedCategory]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleFilterChange = (event) => {
    setShowOnlyNotApproved(event.target.checked);
    setPage(1); // Reset to the first page when filter changes
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1); // Reset to the first page when category changes
  };

  const handleExport = async (
    statusFilter = showOnlyNotApproved ? "not_approved" : "",
    categoryFilter = selectedCategory !== "all" ? selectedCategory : ""
  ) => {
    try {
      setExporting(true);
      setError("");
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (categoryFilter) params.append("category", categoryFilter);
      const queryString = params.toString() ? `?${params.toString()}` : "";

      const url = `/csv/export/${upload.id}${queryString}`;
      const response = await api.get(url, { responseType: "blob" });

      let filename = `${upload.file_name.replace(/\.csv$/i, "")}_results.csv`;
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Export error:", err);
      setError("Failed to export results.");
    } finally {
      setExporting(false);
    }
  };

  // Helper to format column names (replace underscores with spaces)
  const formatLabel = (label) => (label || "").replace(/_/g, " ");

  const hasActiveFilters = showOnlyNotApproved || selectedCategory !== "all";

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        Back to Dashboard
      </Button>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", mb: 2, gap: 2 }}>
        <Typography variant="h4">
          Feedback for: {upload.file_name}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
            onClick={() => handleExport(showOnlyNotApproved ? "not_approved" : "", selectedCategory !== "all" ? selectedCategory : "")}
            disabled={exporting}
          >
            {exporting
              ? "Exporting..."
              : (hasActiveFilters ? "Export Filtered" : "Export Results")}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={() => handleExport("", "")}
              disabled={exporting}
            >
              Export All
            </Button>
          )}
        </Box>
      </Box>

      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: "#f0f4f8",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <FormControlLabel
          control={
            <Switch checked={showOnlyNotApproved} onChange={handleFilterChange} />
          }
          label="Show only items that are not approved"
        />

        {availableCategories.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 220, bgcolor: "background.paper" }}>
              <InputLabel id="category-filter-label">Filter by Category</InputLabel>
              <Select
                labelId="category-filter-label"
                id="category-filter"
                value={selectedCategory}
                label="Filter by Category"
                onChange={handleCategoryChange}
              >
                <MenuItem value="all">
                  <em>All Categories ({availableCategories.length})</em>
                </MenuItem>
                {availableCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedCategory !== "all" && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<FilterListIcon />}
                onClick={() => {
                  setSelectedCategory("all");
                  setPage(1);
                }}
              >
                Clear Category
              </Button>
            )}
          </Box>
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f9f9f9" }}>
        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && (
          <>
            {feedback.length > 0 ? (
              feedback.map((qaItem) => {
                const itemCategory = qaItem.metadata
                  ? (qaItem.metadata.category || qaItem.metadata.Category || qaItem.metadata.CATEGORY)
                  : null;

                return (
                  <Paper key={qaItem.id} elevation={3} sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
                        <Typography variant="body1" color="text.secondary">
                          Item #{qaItem.id}
                        </Typography>
                        {itemCategory && (
                          <Chip
                            label={`Category: ${itemCategory}`}
                            color="info"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      <Typography
                        variant="body1"
                        component="p"
                        sx={{ mt: 1 }}
                      >
                        <strong>{formatLabel(upload.question_column)}:</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        paragraph
                        dangerouslySetInnerHTML={{
                          __html: (qaItem.metadata && qaItem.metadata.question_html) ? qaItem.metadata.question_html : qaItem.text,
                        }}
                      />

                      <Typography
                        variant="body1"
                        component="p"
                        color="text.primary"
                        sx={{ mt: 1 }}
                      >
                        <strong>{formatLabel(upload.answer_column)}:</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        paragraph
                        dangerouslySetInnerHTML={{
                          __html: (qaItem.metadata && qaItem.metadata.answer_html) ? qaItem.metadata.answer_html : qaItem.answer,
                        }}
                      />

                      {/* Show extra metadata columns if available (excluding html renderers and category) */}
                      {qaItem.metadata && typeof qaItem.metadata === 'object' && Object.keys(qaItem.metadata).filter(k => k !== upload.question_column && k !== upload.answer_column && k !== 'question_html' && k !== 'answer_html' && k.toLowerCase() !== 'category').length > 0 && (
                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#eef2f6", borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary", display: "block", mb: 0.5 }}>
                            Other Columns / Metadata:
                          </Typography>
                          {Object.entries(qaItem.metadata)
                            .filter(([k]) => k !== upload.question_column && k !== upload.answer_column && k !== 'question_html' && k !== 'answer_html' && k.toLowerCase() !== 'category')
                            .map(([key, val]) => (
                              <Typography key={key} variant="body2" sx={{ mt: 0.5 }}>
                                <strong>{formatLabel(key)}:</strong> {String(val)}
                              </Typography>
                            ))
                          }
                        </Box>
                      )}
                    </Box>
                    <Divider />
                    <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 1 }}>
                      <RateReviewIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        Reviews ({qaItem.reviews.length})
                      </Typography>
                    </Box>
                    {qaItem.reviews.length > 0 ? (
                      <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                        {qaItem.reviews.map((review) => (
                          <ListItem key={review.id} divider>
                            <ListItemAvatar>
                              <Avatar>
                                <AccountCircleIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                                  <Typography
                                    component="span"
                                    variant="body1"
                                    sx={{ fontWeight: "bold", mr: 1.5 }}
                                    title={review.reviewer_email}
                                  >
                                    {review.reviewer_email}
                                  </Typography>
                                  <Chip
                                    label={review.status}
                                    color={review.status === "approved" ? "success" : "error"}
                                    size="small"
                                  />
                                </Box>
                              }
                              secondary={
                                <>
                                  <Typography
                                    sx={{ display: "block", fontStyle: "italic", mt: 0.5 }}
                                    component="span"
                                    variant="body2"
                                    color="text.primary"
                                  >
                                    {review.message ? `"${review.message}"` : "No message provided."}
                                  </Typography>
                                  {new Date(review.created_at).toLocaleString()}
                                </>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, ml: 1 }}>
                        No reviews for this item yet.
                      </Typography>
                    )}
                  </Paper>
                );
              })
            ) : (
              <Typography>
                {hasActiveFilters
                  ? "No feedback items match the selected filters."
                  : "No feedback received for this upload yet."}
              </Typography>
            )}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default FeedbackViewer;
